import { XClose } from "@untitledui/icons";
import type { FC, ReactElement, ReactNode } from "react";
import { useId } from "react";
import { DialogTrigger as AriaDialogTrigger } from "react-aria-components";
import { Heading } from "@/components/Atoms/Heading/Heading";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";

export const FeatureModalSize = {
	Sm: "sm",
	Md: "md",
	Lg: "lg",
} as const;

export type FeatureModalSize =
	(typeof FeatureModalSize)[keyof typeof FeatureModalSize];

export const FeatureModalTone = {
	Brand: "brand",
	Gray: "gray",
	Error: "error",
	Warning: "warning",
	Success: "success",
} as const;

export type FeatureModalTone =
	(typeof FeatureModalTone)[keyof typeof FeatureModalTone];

const sizeMaxWidth: Record<FeatureModalSize, string> = {
	[FeatureModalSize.Sm]: "sm:max-w-[300px]",
	[FeatureModalSize.Md]: "sm:max-w-[500px]",
	[FeatureModalSize.Lg]: "sm:max-w-[768px]",
};

export interface FeatureModalProps {
	/** Controlled open state. */
	isOpen: boolean;
	/** Called when open state should change (close / Escape / overlay). */
	onOpenChange: (open: boolean) => void;
	/** Modal title (Heading H3). */
	title: string;
	/**
	 * Supporting body copy under the title.
	 * Pass a ReactNode to bold an entity name mid-sentence.
	 */
	description: ReactNode;
	/** Featured icon (Figma Featured slot). */
	icon: FC<{ className?: string }>;
	/**
	 * FeaturedIcon color token.
	 * @default "error"
	 */
	tone?: FeatureModalTone;
	/**
	 * Panel width: sm 300 / md 500 / lg 768.
	 * @default "md"
	 */
	size?: FeatureModalSize;
	/**
	 * Secondary (safe) action label.
	 * @default "No"
	 */
	secondaryLabel?: string;
	/**
	 * Primary action label.
	 * @default "Yes"
	 */
	primaryLabel?: string;
	/** Called when secondary is pressed (also closes by default). */
	onSecondary?: () => void;
	/** Called when primary is pressed. */
	onPrimary: () => void | Promise<void>;
	/** Primary button loading state. */
	isPrimaryLoading?: boolean;
	/** Extra disable for the primary action, such as an unmatched confirmation field. */
	isPrimaryDisabled?: boolean;
	/** Test id for the primary action. */
	primaryTestId?: string;
	/** Optional body under the description, such as a confirmation field. */
	content?: ReactNode;
	/**
	 * Primary button color.
	 * @default "primary-destructive"
	 */
	primaryColor?:
		| "primary"
		| "secondary"
		| "primary-destructive"
		| "secondary-destructive";
	/**
	 * Allow dismiss via overlay click / Escape.
	 * @default false for destructive flows
	 */
	isDismissable?: boolean;
	/** Show footer actions. @default true */
	showActions?: boolean;
	/** Accessible name for the close control. @default "Close" */
	closeAriaLabel?: string;
	className?: string;
	"data-testid"?: string;
}

/**
 * Figma Modal — Type Feature (3276:7426 / 4564:30998).
 * Message-led confirmation dialog with FeaturedIcon, heading, support text, and dual actions.
 *
 * Pair with Overlay (ModalOverlay). Use for confirmations, success, warnings, and announcements.
 * Delete Job / Delete Ticket / Delete Note wrappers use this with Type=Feature tone=error.
 */
export function FeatureModal({
	isOpen,
	onOpenChange,
	title,
	description,
	icon,
	tone = FeatureModalTone.Error,
	size = FeatureModalSize.Md,
	secondaryLabel = "No",
	primaryLabel = "Yes",
	onSecondary,
	onPrimary,
	isPrimaryLoading = false,
	isPrimaryDisabled = false,
	primaryTestId,
	content,
	primaryColor = "primary-destructive",
	isDismissable = false,
	showActions = true,
	closeAriaLabel = "Close",
	className,
	"data-testid": dataTestId = "feature-modal",
}: FeatureModalProps): ReactElement {
	const titleId = useId();
	const descriptionId = useId();

	const handleSecondary = () => {
		onSecondary?.();
		onOpenChange(false);
	};

	const handlePrimary = async () => {
		await onPrimary();
	};

	const handleClose = () => {
		onOpenChange(false);
	};

	return (
		<AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
			<ModalOverlay isDismissable={isDismissable && !isPrimaryLoading}>
				<Modal className={cx("w-full", sizeMaxWidth[size])}>
					<Dialog
						aria-labelledby={titleId}
						aria-describedby={descriptionId}
						className="outline-hidden"
					>
						<div
							className={cx(
								"relative flex w-full flex-col items-center rounded-2xl border border-primary bg-primary p-6 shadow-lg",
								className,
							)}
							data-testid={dataTestId}
							data-size={size}
							data-tone={tone}
						>
							{/* Close — Figma Modal header: absolute row, end-aligned, 12px inset */}
							<div className="absolute inset-x-0 top-0 z-10 flex items-start justify-end p-3">
								<IconButton
									aria-label={closeAriaLabel}
									hierarchy="link"
									size="sm"
									shape="round"
									icon={(iconProps) => (
										<XClose {...iconProps} strokeWidth={2.5} />
									)}
									tooltip={closeAriaLabel}
									isDisabled={isPrimaryLoading}
									onClick={handleClose}
									className="size-9! p-2! text-primary *:data-icon:text-primary"
								/>
							</div>

							{/* Body */}
							<div className="flex w-full flex-col items-center gap-5 px-0 pt-2 pb-10">
								<FeaturedIcon
									icon={icon}
									size="xl"
									color={tone}
									theme="light"
								/>

								<div className="flex w-full flex-col items-center gap-1 text-center">
									<Heading id={titleId} level="h3" className="text-center">
										{title}
									</Heading>
									<div
										id={descriptionId}
										className="w-full text-center text-lg font-medium leading-7 text-secondary"
									>
										{description}
									</div>
								</div>
								{content ? <div className="w-full text-left">{content}</div> : null}
							</div>

							{/* Footer — Secondary + Primary */}
							{showActions ? (
								<div className="flex w-full flex-col-reverse gap-4 sm:flex-row sm:items-center">
									<div className="min-w-0 flex-1">
										<Button
											color="secondary"
											size="lg"
											className="w-full"
											onClick={handleSecondary}
											isDisabled={isPrimaryLoading}
										>
											{secondaryLabel}
										</Button>
									</div>
									<div className="min-w-0 flex-1">
										<Button
											color={primaryColor}
											size="lg"
											className="w-full"
											data-testid={primaryTestId}
											onClick={() => {
												void handlePrimary();
											}}
											isLoading={isPrimaryLoading}
											isDisabled={isPrimaryLoading || isPrimaryDisabled}
											showTextWhileLoading
										>
											{primaryLabel}
										</Button>
									</div>
								</div>
							) : null}
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
}
